<template>
  <div :class="$style.filterTabs">
    <div :class="$style.tabsContainer">
      <!-- 使用v-for循环渲染标签 -->
      <div
        v-for="tab in tabList"
        :key="tab.key"
        :class="getTabClass(tab.key)"
        @click="handleTabClick(tab.key)"
      >
        <span :class="$style.text">{{ tab.name }}</span>

        <!-- 排序指示器 -->
        <div v-if="tab.sortable" :class="$style.indicators">
          <x-icon :class="getIndicatorClass(tab.key, 'up')" name="caret-up" />
          <x-icon
            :class="getIndicatorClass(tab.key, 'down')"
            name="caret-down"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'

  // 排序方向枚举
  const SORT_ORDER = {
    ASC: 'asc',
    DESC: 'desc',
    NONE: '',
  }

  // 标签类型枚举
  const TAB_TYPES = {
    COMPREHENSIVE: 'comprehensive',
    SALES: 'sales',
    PRICE: 'price',
  }

  export default Vue.extend({
    name: 'FilterTabs',

    // 添加props定义
    props: {
      defaultActive: {
        type: String,
        default: TAB_TYPES.COMPREHENSIVE,
        validator: value => Object.values(TAB_TYPES).includes(value),
      },
    },

    data() {
      return {
        activeTab: this.defaultActive || TAB_TYPES.COMPREHENSIVE,
        // 标签配置列表（配置化管理）
        tabList: [
          {
            key: TAB_TYPES.COMPREHENSIVE,
            name: '综合',
            sortable: false,
          },
          {
            key: TAB_TYPES.SALES,
            name: '销量',
            sortable: true,
          },
          {
            key: TAB_TYPES.PRICE,
            name: '价格',
            sortable: true,
          },
        ],
        // 统一的排序状态管理
        sortStates: {
          [TAB_TYPES.SALES]: SORT_ORDER.NONE,
          [TAB_TYPES.PRICE]: SORT_ORDER.NONE,
        },
      }
    },

    computed: {
      // 计算属性：获取当前排序状态
      currentSortState() {
        return this.sortStates[this.activeTab] || SORT_ORDER.NONE
      },

      // 计算属性：是否有激活的排序
      hasActiveSort() {
        return Object.values(this.sortStates).some(
          state => state !== SORT_ORDER.NONE,
        )
      },
    },

    // 添加watch监听
    watch: {
      defaultActive: {
        handler(newVal) {
          if (newVal && Object.values(TAB_TYPES).includes(newVal)) {
            this.resetToTab(newVal)
          }
        },
        immediate: false,
      },
    },

    methods: {
      // 统一的标签点击处理
      handleTabClick(tabKey) {
        const tab = this.getTabConfig(tabKey)
        if (!tab) return

        // 如果是当前激活标签且支持排序，则切换排序
        if (this.activeTab === tabKey && tab.sortable) {
          this.toggleSort(tabKey)
        } else {
          // 切换到新标签
          this.switchTab(tabKey)
        }
      },

      // 切换标签
      switchTab(tabKey) {
        const previousTab = this.activeTab
        this.activeTab = tabKey

        // 重置其他标签的排序状态
        this.resetOtherSorts(tabKey)

        // 如果新标签支持排序，设置默认排序
        const tab = this.getTabConfig(tabKey)
        if (tab && tab.sortable) {
          this.sortStates[tabKey] = SORT_ORDER.DESC
        }

        // 触发事件
        this.emitSortChange(tabKey, previousTab)
      },

      // 切换排序方向
      toggleSort(tabKey) {
        const currentSort = this.sortStates[tabKey]
        this.sortStates[tabKey] =
          currentSort === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC

        // 触发事件
        this.emitSortChange(tabKey)
      },

      // 重置其他标签的排序状态
      resetOtherSorts(excludeKey) {
        Object.keys(this.sortStates).forEach(key => {
          if (key !== excludeKey) {
            this.sortStates[key] = SORT_ORDER.NONE
          }
        })
      },

      // 获取标签配置
      getTabConfig(tabKey) {
        return this.tabList.find(tab => tab.key === tabKey)
      },

      // 获取标签样式类（优化性能）
      getTabClass(tabKey) {
        return [
          this.$style.item,
          this.activeTab === tabKey && this.$style.active,
        ].filter(Boolean)
      },

      // 获取指示器样式类（简化逻辑）
      getIndicatorClass(tabKey, direction) {
        const classes = [this.$style.indicator]

        // 只有激活标签且有排序状态时才显示选中样式
        if (this.activeTab === tabKey) {
          const sortState = this.sortStates[tabKey]
          const isSelected =
            (direction === 'up' && sortState === SORT_ORDER.ASC) ||
            (direction === 'down' && sortState === SORT_ORDER.DESC)

          if (isSelected) {
            classes.push(this.$style.selected)
          }
        }

        return classes
      },

      // 统一的事件触发
      emitSortChange(tabKey, previousTab = null) {
        const sortOrder = this.sortStates[tabKey] || SORT_ORDER.DESC

        // 触发通用排序事件
        this.$emit('sort-change', {
          type: tabKey,
          order: sortOrder,
          previousType: previousTab,
        })
      },

      // 新增：重置到指定标签的方法
      resetToTab(tabKey = TAB_TYPES.COMPREHENSIVE) {
        this.activeTab = tabKey
        // 重置所有排序状态
        Object.keys(this.sortStates).forEach(key => {
          this.sortStates[key] = SORT_ORDER.NONE
        })
      },

      // 优化：公共方法重置所有排序
      resetAllSorts() {
        this.resetToTab(TAB_TYPES.COMPREHENSIVE)
      },

      // 公共方法：设置排序状态
      setSortState(tabKey, sortOrder) {
        if (Object.prototype.hasOwnProperty.call(this.sortStates, tabKey)) {
          this.sortStates[tabKey] = sortOrder
          this.activeTab = tabKey
        }
      },
    },
  })
</script>

<style lang="scss" module>
  .filterTabs {
    background-color: #fff;

    .tabsContainer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 24px;

      .item {
        display: flex;
        align-items: center;
        transition: all 0.2s ease;

        .text {
          font-size: 14px;
          color: #666;
          font-weight: 400;
          transition: color 0.2s ease;
        }

        .indicators {
          margin-left: 3px;
          display: flex;
          flex-direction: column;
          align-items: center;

          .indicator {
            font-size: 12px;
            color: #666;
            transition: color 0.2s ease;

            &:last-child {
              margin-top: -6px;
            }

            &.selected {
              color: #ff840a;
            }
          }
        }

        &.active {
          .text {
            color: #ff840a;
            font-weight: 500;
          }
        }
      }
    }
  }
</style>
